import { useQuery } from "@tanstack/solid-query";
import { Component, createSignal, Show } from "solid-js";

import gqlClient from "../../schema/gqlAuthorizedRequest.js";
import { graphql } from "../../schema/index.js";
import { countryCodeMap } from "../../utils/country-codes.js";
import { Button } from "../common/Button.js";
import DataLoadingSwitch from "../common/DataLoadingSwitch.js";
import FormRow from "../common/FormRow.js";
import { useToast } from "../toast/index.js";
import { useForm } from "./FormContext.js";
import Input from "./Input.js";
import Select from "./Select.js";

interface SingleAddressChangeFormProps {
  name: string;
  editable?: boolean;
  inline?: boolean;
  containerClass?: string;
  countryOptions?: {
    name: string;
    value: string | null;
    countryCodeValue?: string;
  }[];
}

const findAddressByPostcodeQuery = graphql(`
  query findAddressByPostcode($postcode: String!) {
    findAddressByPostcode(postcode: $postcode) {
      postcode
      addresses {
        line_1
        line_2
        line_3
        line_4
        town_or_city
        county
        country
      }
    }
  }
`);

export const countryOptions = Object.entries(countryCodeMap)
  .sort()
  .map(([name, value]) => ({ name, value }));

const AddressInput: Component<SingleAddressChangeFormProps> = (props) => {
  const [manualEntry, setManualEntry] = createSignal(true);
  const toast = useToast();

  const formContext = useForm();

  const postcodeSearch = useQuery(() => ({
    queryKey: ["postcode", formContext?.getData(`${props.name}.postcode`)],
    queryFn: async () => {
      if (!formContext) {
        toast.error("Configuration error", "Form context is missing");
        return;
      }
      const result = await gqlClient.request(findAddressByPostcodeQuery, {
        postcode: formContext.getData(`${props.name}.postcode`),
      });
      const foundAddresses = result.findAddressByPostcode.addresses.map(
        (address) => ({
          value: {
            postcode: result.findAddressByPostcode.postcode,
            line1: address.line_1,
            line2: [address.line_2, address.line_3, address.line_4]
              .filter((line) => line.length)
              .join(", "),
            townOrCity: address.town_or_city,
            county: address.county,
            country: getCountryValue(address.country),
          },
          name: `${[
            address.line_1,
            address.line_2,
            address.line_3,
            address.line_4,
          ]
            .filter((line) => line.length)
            .join(", ")}, ${address.town_or_city}, ${
            address.county
          }, ${getCountryValue(address.country)}`,
        }),
      );
      return foundAddresses;
    },
    enabled: false,
  }));
  const addressSelected = (value: any) => {
    formContext?.setData(`${props.name}`, value);
    setManualEntry(true);
  };

  const getCountryValue = (value: string) => {
    if (!props.countryOptions) {
      return value;
    }
    const trimmedValue = value.toLowerCase().trim();
    const country = props.countryOptions.find((country) => {
      if (country.countryCodeValue) {
        return country.countryCodeValue.toLowerCase().trim() === trimmedValue;
      }
      return country.value?.toLocaleLowerCase().trim() === trimmedValue;
    });
    return country?.value;
  };

  const showPostcodeSearch = () => {
    if (props.editable === false) {
      return false;
    }
    return !manualEntry();
  };

  const searchPostcode = () => {
    setManualEntry(false);
    postcodeSearch.refetch();
  };

  return (
    <>
      <FormRow
        label="Postcode"
        inline={props.inline}
        containerClass={props.containerClass}
      >
        <div class="flex w-full flex-row items-baseline gap-2">
          <Input
            autocomplete="off"
            name={`${props.name}.postcode`}
            editable={props.editable}
          />
          <Show when={props.editable !== false}>
            <Button onClick={searchPostcode} color="secondary">
              Search
            </Button>
          </Show>
        </div>
      </FormRow>
      <Show
        when={!showPostcodeSearch()}
        fallback={
          <FormRow
            label=""
            inline={props.inline}
            containerClass={props.containerClass}
          >
            <DataLoadingSwitch
              loader={postcodeSearch}
              errorContent={
                <div class="text-sm text-red-500">
                  <p>Failed to load addresses</p>
                </div>
              }
            >
              <Select
                showSearchBar={true}
                options={postcodeSearch.data!}
                onChange={addressSelected}
                placeholder="Select address"
              />
            </DataLoadingSwitch>
            <div class="mt-4">
              <Button
                onClick={() => {
                  setManualEntry(true);
                }}
              >
                Enter manually
              </Button>
            </div>
          </FormRow>
        }
      >
        <FormRow
          label="Line 1"
          inline={props.inline}
          containerClass={props.containerClass}
        >
          <Input
            autocomplete="off"
            name={`${props.name}.line1`}
            editable={props.editable}
          />
        </FormRow>
        <FormRow
          label="Line 2"
          inline={props.inline}
          containerClass={props.containerClass}
        >
          <Input
            autocomplete="off"
            name={`${props.name}.line2`}
            editable={props.editable}
          />
        </FormRow>
        <FormRow
          label="Town or city"
          inline={props.inline}
          containerClass={props.containerClass}
        >
          <Input
            autocomplete="off"
            name={`${props.name}.townOrCity`}
            editable={props.editable}
          />
        </FormRow>
        <FormRow
          label="County"
          inline={props.inline}
          containerClass={props.containerClass}
        >
          <Input
            autocomplete="off"
            name={`${props.name}.county`}
            editable={props.editable}
          />
        </FormRow>
        <FormRow
          label="Country"
          inline={props.inline}
          containerClass={props.containerClass}
        >
          <Select
            options={props.countryOptions ?? countryOptions}
            showSearchBar={true}
            name={`${props.name}.country`}
            editable={props.editable}
          />
        </FormRow>
      </Show>
    </>
  );
};

export default AddressInput;
